import { useCallback, useState } from "react";
import {
  defaultOccurrenceColWidths,
  type OccurrenceVariant,
} from "../components/KksOccurrenceRow";

const STORAGE_KEY = "pid-kks-extractor-preferences";

interface UserPreferences {
  col1Width: number;
  col2Width: number;
  shortcutsEnabled: boolean;
  anomalyColWidths: number[];
  searchColWidths: number[];
}

const DEFAULTS: UserPreferences = {
  col1Width: 280,
  col2Width: 280,
  shortcutsEnabled: false,
  anomalyColWidths: defaultOccurrenceColWidths("anomalies"),
  searchColWidths: defaultOccurrenceColWidths("search"),
};

/** Preference key holding the persisted column widths for each occurrence table. */
export const OCCURRENCE_WIDTHS_KEY: Record<
  OccurrenceVariant,
  "anomalyColWidths" | "searchColWidths"
> = {
  anomalies: "anomalyColWidths",
  search: "searchColWidths",
};

function load(): UserPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

function save(prefs: UserPreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function useUserPreferences() {
  const [prefs, setPrefs] = useState<UserPreferences>(load);

  const update = useCallback((patch: Partial<UserPreferences>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      save(next);
      return next;
    });
  }, []);

  return { prefs, update };
}
